import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {BrandKitStyleTile} from '@Components/brand-kit-style-tile/brand-kit-style-tile';
import {canUserAccessBrandKits, doesBrandHaveAnyAssetType, doesBrandHaveColorsOrFonts} from '@Libraries/brands-library';
import type {BrandKitWithAssets} from '@Components/mystuff-brands/brand-kits.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {changeActiveBrandId} from '@Components/mystuff-brands/brands-thunk';
import {getSortedBrandKitsWithAssets} from '@Hooks/brands/useSortedBrandKitsWithAssets';
import {BrandKitsEmptyMessageType} from '@Components/brand-kits-empty-state-box/brand-kits-empty-state-box.types';
import {BrandKitsEmptyStateBox} from '@Components/brand-kits-empty-state-box/brand-kits-empty-state-box';
import {useBusinessProfileBrandIdFromBrandsStore} from '@Hooks/brands/useBusinessProfileBrandIdFromBrandsStore';
import {useBusinessProfileBrandID} from '@Components/business-profiles-wizard/business-profiles-wizard.hooks';
import type {BrandKitsSectionProps} from './brand-kits-section.types';
import styles from './brand-kits-section.module.scss';
import {useAppDispatch} from '@/hooks';
import {debounce} from '@Utils/misc.util';
import {useBrandKitsWithAssets} from '@Hooks/brands/useBrandKitsWithAssets';
import {useActiveBrandId} from '@Hooks/brands/useActiveBrandId';

export function BrandKitsSection({onClickBrandKit, allowImageSelection = true}: BrandKitsSectionProps): ReactElement<any> {
  const businessProfileBrandID = useBusinessProfileBrandID();
  const businessProfileIdFromBrandsStore = useBusinessProfileBrandIdFromBrandsStore();
  const brandKitsWithAssets: BrandKitWithAssets[] = useBrandKitsWithAssets();
  const activeBrandId = useActiveBrandId();
  const sortedBrandKitsWithAssets = useRef(getSortedBrandKitsWithAssets(brandKitsWithAssets, activeBrandId, businessProfileBrandID || businessProfileIdFromBrandsStore));

  const listRef = useRef<HTMLUListElement>(null);
  const [showLeftIcon, setShowLeftIcon] = useState(false);
  const [showRightIcon, setShowRightIcon] = useState(false);
  const dispatch = useAppDispatch();

  const removeIncompleteBrandKitsFromMap = (): BrandKitWithAssets[] => {
    return sortedBrandKitsWithAssets.current.filter((brandInfo) => {
      return allowImageSelection ? doesBrandHaveAnyAssetType(brandInfo.brandKit.brandId) : doesBrandHaveColorsOrFonts(brandInfo.brandKit.brandId);
    });
  };

  const filteredBrandKitsWithAssets = removeIncompleteBrandKitsFromMap();

  const onBrandKitClick = (brandKitId: string, colors: string[], selectedImage?: string, selectedFontFamily?: string): void => {
    void dispatch(changeActiveBrandId({newActiveBrandId: brandKitId}));
    onClickBrandKit(colors[0], selectedFontFamily, selectedImage);
  };

  const getTiles = (): ReactElement<any>[] => {
    return filteredBrandKitsWithAssets.map((brandInfo) => {
      return (
        <li key={brandInfo.brandKit.brandId} className={styles.brandKitListItem}>
          <BrandKitStyleTile
            onClickBrandKit={onBrandKitClick}
            brandKit={brandInfo.brandKit}
            brandColors={brandInfo.colors}
            brandLogos={brandInfo.logos}
            brandFonts={brandInfo.fonts}
            showImage={allowImageSelection}
          />
        </li>
      );
    });
  };

  const isFilteredBKListEmpty = (): boolean => {
    return filteredBrandKitsWithAssets.length === 0;
  };

  const doIncompleteBrandsExist = (): boolean => {
    return isFilteredBKListEmpty() && brandKitsWithAssets.length > 0;
  };

  const isInEmptyState = (): boolean => {
    return brandKitsWithAssets.length === 0 || isFilteredBKListEmpty();
  };

  const onLeftIconClick = (): void => {
    listRef.current?.scrollBy({
      left: -listRef.current.offsetWidth,
      behavior: 'smooth',
    });
  };

  const onRightIconClick = (): void => {
    listRef.current?.scrollBy({
      left: listRef.current.offsetWidth,
      behavior: 'smooth',
    });
  };

  const doesListHaveScroll = (): boolean => {
    if (!listRef.current) {
      return false;
    }

    return listRef.current.scrollWidth > listRef.current.offsetWidth;
  };

  const hasListScrolledToStart = (): boolean => {
    if (!listRef.current) {
      return false;
    }

    return listRef.current.scrollLeft < listRef.current.offsetWidth;
  };

  const hasListScrolledToEnd = (): boolean => {
    if (!listRef.current) {
      return false;
    }

    return listRef.current.scrollLeft >= listRef.current.scrollWidth - listRef.current.clientWidth;
  };

  const onCarouselScroll = (): void => {
    if (!listRef.current) {
      return;
    }

    if (hasListScrolledToStart()) {
      setShowLeftIcon(false);
    } else {
      setShowLeftIcon(true);
    }

    if (hasListScrolledToEnd()) {
      setShowRightIcon(false);
    } else {
      setShowRightIcon(true);
    }
  };

  useEffect(() => {
    if (!listRef.current) {
      return;
    }

    if (doesListHaveScroll()) {
      setShowRightIcon(true);
    }

    const debouncedHandler = debounce(onCarouselScroll, 100);
    listRef.current.addEventListener('scroll', debouncedHandler, {passive: true});

    return (): void => {
      listRef.current?.removeEventListener('scroll', debouncedHandler);
    };
  }, []);

  const getCarouselList = (): ReactElement<any> => {
    const iconBaseClasses = `_position-absolute ${styles.carouselIcon}`;

    return (
      <div className="_full-width _position-relative">
        <Icon
          className={`${iconBaseClasses} ${styles.leftIcon} ${showLeftIcon ? '_opacity-1' : '_opacity-0'}`}
          icon="icon-caret-left"
          type={IconType.SHADOW}
          size={IconSize.SIZE_ICON_16}
          shape={IconShape.CIRCLE}
          onClick={onLeftIconClick}
          ariaLabel={window.i18next.t('pmwjs_previous')}
          isUsedAsButton
        />
        <ul ref={listRef} className={`flexbox spacing-m-t-3 ${styles.brandKitsList}`}>
          {getTiles()}
        </ul>
        <Icon
          className={`${iconBaseClasses} ${styles.rightIcon} ${showRightIcon ? '_opacity-1' : '_opacity-0'} `}
          icon="icon-caret-right"
          type={IconType.SHADOW}
          size={IconSize.SIZE_ICON_16}
          shape={IconShape.CIRCLE}
          onClick={onRightIconClick}
          ariaLabel={window.i18next.t('pmwjs_next')}
          isUsedAsButton
        />
      </div>
    );
  };

  const brandKitsEmptyStateType = doIncompleteBrandsExist() ? BrandKitsEmptyMessageType.INCOMPLETE_BRAND_KITS : BrandKitsEmptyMessageType.NO_BRAND_KITS;
  return (
    <section className="flex-v-row spacing-m-b-10">
      <h2 className="body-md-bold content-body _unpadded-top spacing-p-b-1 border-s-standard _no-border-top _no-border-left _no-border-right flex-row-align-center">
        {window.i18next.t('pmwjs_brand_kits')}
        {!canUserAccessBrandKits() ? <Icon icon="icon-crown" size={IconSize.SIZE_ICON_16} className={`spacing-m-l-2 ${styles.premiumIcon}`} /> : null}
      </h2>
      {isInEmptyState() ? (
        <BrandKitsEmptyStateBox
          emptyMessageType={brandKitsEmptyStateType}
          canUserAccessBrandKits={canUserAccessBrandKits()}
          className="spacing-m-t-6"
          ctaClassName="spacing-m-t-3"
          customEmptyStateText={
            brandKitsEmptyStateType === BrandKitsEmptyMessageType.NO_BRAND_KITS ? window.i18next.t('pmwjs_instantly_add_your_brand_colors_and_logos_everywhere') : ''
          }
        />
      ) : (
        getCarouselList()
      )}
    </section>
  );
}
